import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useUsers } from "../../hooks/useUsers";
import MainPageWrapper from "../../../../../../components/MainPageWrapper/MainPageWrapper";
import UserListFilters from "../UserListFilters/UserListFilters";
import { Button } from "@/components/Button/Button";
import { User } from "../../slices/usersSlice";
import { createUsersColumns } from "../UserColumns/UserColumns";
import DataTable from "@/components/Datatable/Datatable";
import DeactivateUserModal from "../DeactivateUserModal/DeactivateUserModal";
import ReactivateUserModal from "../ReactivateUserModal/ReactivateUserModal";

const UserList: React.FC = () => {
  const {
    users,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
    filters,
    userToDeactivate,
    isDeactivating,
    userToReactivate,
    isReactivating,
    openDeactivateUser,
    closeDeactivateUser,
    openReactivateUser,
    closeReactivateUser,
    confirmDeactivateUser,
    confirmReactivateUser,
  } = useUsers();

  const navigate = useNavigate();

  const handleEdit = useCallback(
    (uuid: string) => {
      navigate(`/hub/users/${uuid}/edit`);
    },
    [navigate],
  );

  const columns = useMemo(
    () =>
      createUsersColumns({
        handleEdit,
        handleDeactivateUser: openDeactivateUser,
        handleReactivateUser: openReactivateUser,
      }),
    [handleEdit, openDeactivateUser, openReactivateUser],
  );

  return (
    <MainPageWrapper
      actions={
        <Button onClick={() => navigate("/hub/users/create")}>
          Create User
        </Button>
      }
      error={error}
      title="Users"
    >
      <UserListFilters
        filters={filters}
        onFilterChange={(
          filterKey: string,
          value: string | number | undefined,
        ) => {
          handleFilterChange(filterKey, value);
          handlePaginationChange({
            pageIndex: 0,
            pageSize: pagination.pageSize,
          });
        }}
      />

      <div className="relative">
        <DataTable<User>
          columns={columns}
          data={users}
          error={error}
          loading={loading}
          noDataMessage="No users found"
          onPageChange={(newPageIndex, newPageSize) =>
            handlePaginationChange({
              pageIndex: newPageIndex,
              pageSize: newPageSize,
            })
          }
          onSortingChange={handleSortingChange}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          rowKeyExtractor={(item) => item.id}
          sorting={sorting}
          totalCount={totalCount}
        />
      </div>

      <DeactivateUserModal
        handleDeactivate={confirmDeactivateUser}
        isDeactivating={isDeactivating}
        isOpen={userToDeactivate !== null}
        onClose={closeDeactivateUser}
        userName={
          userToDeactivate
            ? `${userToDeactivate.firstName} ${userToDeactivate.lastName}`
            : ""
        }
      />

      <ReactivateUserModal
        handleReactivate={confirmReactivateUser}
        isOpen={userToReactivate !== null}
        isReactivating={isReactivating}
        onClose={closeReactivateUser}
        userName={
          userToReactivate
            ? `${userToReactivate.firstName} ${userToReactivate.lastName}`
            : ""
        }
      />
    </MainPageWrapper>
  );
};

export default UserList;
